/**
 * Componente de Rota Protegida
 * Usa o hook useAuth genérico - independente de provedor
 * Inclui verificação adicional de token para garantir autenticação válida
 */
import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { refreshAccessToken } from '@/features/auth/utils/tokenRefresh';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation("shared");
  const [isRecovering, setIsRecovering] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const verifyAuth = async () => {
      if (isLoading || isAuthenticated || isRecovering || shouldRedirect) {
        return;
      }

      setIsRecovering(true);

      try {
        /** Tenta recuperar sessão via refresh no provider ativo */
        const newToken = await refreshAccessToken();
        if (!newToken) {
          setShouldRedirect(true);
        }
      } catch {
        setShouldRedirect(true);
      } finally {
        setIsRecovering(false);
      }
    };

    /** Apenas faz verificação adicional se não autenticado */
    if (!isLoading && !isAuthenticated && !isRecovering && !shouldRedirect) {
      verifyAuth();
    }
  }, [isAuthenticated, isLoading, isRecovering, shouldRedirect]);

  /** Estado de carregamento */
  if (isLoading || isRecovering) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex items-center space-x-2">
          <span className="h-8 w-8 animate-spin rounded-full border-4 border-visagio-mint border-t-transparent"></span>
          <span className="text-lg">{t("auth.loading")}</span>
        </div>
      </div>
    );
  }

  /** Redirecionar para login se não autenticado */
  if (!isAuthenticated && shouldRedirect) {
    return <Navigate to="/login" replace />;
  }

  /** Autenticado - renderizar filhos */
  if (isAuthenticated) {
    return <>{children}</>;
  }

  /** Ainda verificando */
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex items-center space-x-2">
        <span className="h-8 w-8 animate-spin rounded-full border-4 border-visagio-mint border-t-transparent"></span>
        <span className="text-lg">{t("auth.verifyingAuth")}</span>
      </div>
    </div>
  );
}
